import { SystemTemplateSlug, TenantConfigType } from "@scayla/api-client-fetch";
import { emailFeatureConfig } from "@scayla/core";
import { useQuery } from "@tanstack/vue-query";
import { computed } from "vue";

import { getAccountsQuery } from "@/composables/useProviderAcount";
import { $t } from "@/i18n";
import { localizeBuiltinModule } from "@/i18n/helpers";
import { Routes } from "@/router";
import { useWorkspace } from "@/store/workspace";
import type { NavNode } from "@/utils/navUtils";

import { mergeOrgEntityPermissions } from "./entityPermissions";

export function useSettingsNav() {
  const workspace = useWorkspace();

  // todo #2260 Needs to be reworked after fixing permission issues
  const getEntityPermissions = (entityKey: string) =>
    mergeOrgEntityPermissions(
      workspace.permissions?.scope,
      workspace.orgId ?? workspace.tenant?.organization?.id,
      entityKey,
    );

  const organizationsPermissions = computed(() => getEntityPermissions("organizations"));
  const organizationsSection = computed(() => {
    if (!workspace.permissionsReady) return null;
    if (!organizationsPermissions.value.active) return null;

    return {
      label: $t("common.label.organizations"),
      layoutStoreKey: "organizations",
      route: {
        name: Routes.SETTINGS_ORGANISATIONS,
      },
    };
  });

  const usersPermissions = computed(() => getEntityPermissions("users"));
  const usersSection = computed(() => {
    if (!workspace.permissionsReady) return null;
    if (!usersPermissions.value.active) return null;

    return {
      label: $t("common.label.users"),
      layoutStoreKey: "users",
      route: {
        name: Routes.SETTINGS_USERS,
      },
    };
  });

  const modulePermissions = computed(() => getEntityPermissions("module_acl"));
  const sidebarPermissions = computed(() => getEntityPermissions("layout_section_groups"));
  const modulesSection = computed(() => {
    if (!workspace.permissionsReady) return null;
    if (!modulePermissions.value.active || !workspace.modules) return null;

    const tmplPermissions = computed(() => getEntityPermissions("template_acl"));

    const children: NavNode[] = [
      ...workspace.modules.filter((m) => {
        // TODO: Remove when Email feature is ready for PROD !!
        if (m.id === "5df585db-6e6b-415a-b2ae-2f1a45aabb95" && !emailFeatureConfig.enabled) return false;

        return true;
      }),
    ].map((m) => ({
      label: localizeBuiltinModule(m.builtinModule),
      layoutStoreKey: m.id,
      children: tmplPermissions.value.active
        ? [
            {
              label: $t("common.label.templates"),
              layoutStoreKey: `${m.id}-templates`,
              route: { name: Routes.TEMPLATES, params: { moduleId: m.id } },
              activeRoutes: [
                { name: Routes.TEMPLATES, params: { moduleId: m.id } },
                { path: `/settings/module/${m.id}/folder` },
              ],
            },
            {
              label: $t("common.label.tableBar2"),
              layoutStoreKey: `${m.id}-tableBar`,
              route: { name: Routes.TABLE_BAR, params: { moduleId: m.id } },
            },
          ]
        : [
            {
              label: $t("common.label.tableBar2"),
              layoutStoreKey: `${m.id}-tableBar`,
              route: { name: Routes.TABLE_BAR, params: { moduleId: m.id } },
            },
          ],
    }));

    if (sidebarPermissions.value.active) {
      children.push({
        label: $t("common.label.sidebar"),
        layoutStoreKey: "layout-section-groups",
        route: { name: Routes.LAYOUT_SECTION_GROUPS },
      });
    }

    return {
      label: $t("common.label.modules"),
      layoutStoreKey: "modules",
      children,
    };
  });
  const rolePermissions = computed(() => getEntityPermissions("roles"));
  const roleSection = computed(() => {
    if (!workspace.permissionsReady) return null;
    if (!rolePermissions.value.active) return null;

    return {
      label: $t("common.label.roles"),
      layoutStoreKey: "roles",
      route: {
        name: Routes.SETTINGS_PERMISSIONS_ROLES,
      },
    };
  });
  const userGroupsPermissions = computed(() => getEntityPermissions("usergroups"));
  const userGroupsSection = computed(() => {
    if (!workspace.permissionsReady) return null;
    if (!userGroupsPermissions.value.active) return null;

    return {
      label: $t("common.label.userGroups2"),
      layoutStoreKey: "usergroups",
      route: {
        name: Routes.USER_GROUPS,
      },
    };
  });
  const roleUserLinkPermissions = computed(() => getEntityPermissions("user_role_links"));
  const roleGroupLinkPermissions = computed(() => getEntityPermissions("usergroup_role_links"));
  const roleUserGroupsSection = computed(() => {
    if (!workspace.permissionsReady) return null;
    if (!(roleUserLinkPermissions.value.active || roleGroupLinkPermissions.value.active)) return null;

    return {
      label: $t("common.label.roleUserGroups"),
      layoutStoreKey: "roleUserGroups",
      route: {
        name: Routes.SETTINGS_PERMISSIONS_ROLE_2_USR_GRP,
      },
    };
  });

  const permissionPermissions = computed(() => getEntityPermissions("entity_acl"));
  const permissionSection = computed(() => {
    if (!workspace.permissionsReady) return null;
    if (!permissionPermissions.value.active) return null;

    return {
      label: $t("common.label.permissions"),
      layoutStoreKey: "permissions",
      children: [
        ...(roleSection.value ? [roleSection.value] : []),
        ...(userGroupsSection.value ? [userGroupsSection.value] : []),
        ...(roleUserGroupsSection.value ? [roleUserGroupsSection.value] : []),
      ],
    };
  });

  const categoriesPermissions = computed(() => getEntityPermissions("categories"));
  const publicCategoriesSection = computed(() => {
    if (!workspace.permissionsReady) return null;
    if (!categoriesPermissions.value.active) return null;

    return {
      label: $t("common.label.publicCategories"),
      route: {
        name: Routes.TENANT_PUBLIC_CATEGORIES,
        layoutStoreKey: "publicCategories",
      },
    };
  });

  const customFieldsPermissions = computed(() => getEntityPermissions("fields"));
  const customFieldsSection = computed(() => {
    if (!workspace.permissionsReady) return null;
    if (!customFieldsPermissions.value.active) return null;

    return {
      label: $t("common.label.customFields2"),
      route: { name: Routes.CUSTOM_FIELDS },
      layoutStoreKey: "customFields",
    };
  });

  const fieldGroupsPermissions = computed(() => getEntityPermissions("field_groups"));
  const fieldGroupsSection = computed(() => {
    if (!workspace.permissionsReady) return null;
    if (!fieldGroupsPermissions.value.active) return null;

    return {
      label: $t("common.label.fieldGroups2"),
      route: { name: Routes.FIELD_GROUPS },
      layoutStoreKey: "fieldGroups",
    };
  });

  const recordStatusSetsPermissions = computed(() => getEntityPermissions("record_status_set"));
  const recordStatusSetsSection = computed(() => {
    if (!workspace.permissionsReady) return null;
    if (!recordStatusSetsPermissions.value.active) return null;

    return {
      label: $t("common.label.statusSets"),
      route: { name: Routes.TENANT_RECORD_STATUS_SETS },
      layoutStoreKey: "recordStatusSets",
    };
  });

  const recordPrioritySetsPermissions = computed(() => getEntityPermissions("record_priority_set"));
  const recordPrioritySetsSection = computed(() => {
    if (!workspace.permissionsReady) return null;
    if (!recordPrioritySetsPermissions.value.active) return null;

    return {
      label: $t("common.label.prioritySets"),
      route: { name: Routes.TENANT_RECORD_PRIORITY_SETS },
      layoutStoreKey: "recordPrioritySets",
    };
  });

  const customSequencesPermissions = computed(() => getEntityPermissions("custom_sequences"));
  const customSequencesSection = computed(() => {
    if (!workspace.permissionsReady) return null;
    if (!customSequencesPermissions.value.active) return null;

    return {
      label: $t("common.label.scaylaIdNumber"),
      route: { name: Routes.CUSTOM_SEQUENCES },
      layoutStoreKey: "customSequences",
    };
  });

  const customDataSection = computed(() => {
    if (
      !(
        publicCategoriesSection.value ||
        customFieldsSection.value ||
        fieldGroupsSection.value ||
        recordStatusSetsSection.value ||
        recordPrioritySetsSection.value ||
        customSequencesSection.value
      )
    )
      return null;
    return {
      label: $t("common.label.customData"),
      layoutStoreKey: "customData",
      children: [
        ...(publicCategoriesSection.value ? [publicCategoriesSection.value] : []),
        ...(customFieldsSection.value ? [customFieldsSection.value] : []),
        ...(customSequencesSection.value ? [customSequencesSection.value] : []),
        ...(fieldGroupsSection.value ? [fieldGroupsSection.value] : []),
        ...(recordStatusSetsSection.value ? [recordStatusSetsSection.value] : []),
        ...(recordPrioritySetsSection.value ? [recordPrioritySetsSection.value] : []),
      ],
    };
  });

  const accountingSection = computed(() => {
    if (!workspace.permissionsReady) return null;
    const children = [];

    const accountTemplate = workspace.accountingSettingsTemplates?.items.find(
      (t) => t.systemTemplateSlug === SystemTemplateSlug.SYSTEM_TEMPLATE_SLUG_ACCOUNTING_ACCOUNT,
    );

    const reportingStructuresTemplate = workspace.accountingSettingsTemplates?.items.find(
      (t) => t.systemTemplateSlug === SystemTemplateSlug.SYSTEM_TEMPLATE_SLUG_ACCOUNTING_ACCOUNTS_STRUCTURE,
    );

    const costCenterTemplate = workspace.accountingSettingsTemplates?.items.find(
      (t) => t.systemTemplateSlug === SystemTemplateSlug.SYSTEM_TEMPLATE_SLUG_ACCOUNTING_COST_CENTER,
    );

    if (accountTemplate?.id) {
      children.push({
        label: $t("common.label.accountList"),
        layoutStoreKey: "accountsList",
        route: {
          name: Routes.MODULE,
          params: { moduleId: "974f96ca-7819-46a7-be9d-c00706e69828" },
          query: {
            templateId: accountTemplate.id,
            exclusiveTemplateView: true,
          },
        },
      });
    }

    if (reportingStructuresTemplate?.id) {
      children.push({
        label: $t("common.label.balanceSheetStructures"),
        layoutStoreKey: "accountsStructuresList",
        route: {
          name: Routes.MODULE,
          params: { moduleId: "974f96ca-7819-46a7-be9d-c00706e69828" },
          query: {
            templateId: reportingStructuresTemplate.id,
            exclusiveTemplateView: true,
          },
        },
      });
    }

    if (costCenterTemplate?.id) {
      children.push({
        label: $t("common.label.costCenters"),
        layoutStoreKey: "accountingCostCenters",
        route: {
          name: Routes.MODULE,
          params: { moduleId: "974f96ca-7819-46a7-be9d-c00706e69828" },
          query: {
            templateId: costCenterTemplate.id,
            exclusiveTemplateView: true,
          },
        },
      });
    }
    // TODO: Uncomment when tax rates view is implemented
    // children.push({
    //   label: $t('common.label.taxRates'),
    //   layoutStoreKey: "taxRates",
    //   route: { name: Routes.TENANT_ACCOUNTING_TAX_RATES },
    // });

    return {
      label: $t("common.label.accounting"),
      layoutStoreKey: "accounting",
      children,
    };
  });

  const servicesPermissions = computed(() => getEntityPermissions("tenant_configs"));
  const hasServiceConfigurationPermission = computed(() => servicesPermissions.value.active);

  const serviceAccountsQueryConfig = getAccountsQuery(TenantConfigType.MAILBOX_ACCOUNT);
  const { data: serviceAccountsList } = useQuery({
    ...serviceAccountsQueryConfig,
    enabled: computed(
      () => !!workspace.tenantId && !hasServiceConfigurationPermission.value && workspace.permissionsReady,
    ),
    retry: false,
  });

  const isServiceIntegrationVisible = computed(() => hasServiceConfigurationPermission.value);
  const isServiceAccountVisible = computed(
    () =>
      hasServiceConfigurationPermission.value ||
      (Array.isArray(serviceAccountsList.value) && serviceAccountsList.value.length > 0),
  );
  const servicesSection = computed(() => {
    if (!workspace.permissionsReady) return null;
    if (!isServiceAccountVisible.value && !isServiceIntegrationVisible.value) return null;
    const children = [
      ...(isServiceIntegrationVisible.value
        ? [
            {
              label: $t("common.label.serviceIntegrations"),
              layoutStoreKey: "integration",
              route: { name: Routes.SETTINGS_SERVICE_INTEGRATION },
            },
          ]
        : []),
      ...(isServiceAccountVisible.value
        ? [
            {
              label: $t("common.label.serviceAccounts"),
              layoutStoreKey: "serviceAccount",
              route: { name: Routes.SETTINGS_SERVICE_ACCOUNT },
            },
          ]
        : []),
    ];
    return {
      label: $t("common.label.services"),
      layoutStoreKey: "services",
      children,
    };
  });

  const aiSettingsPermissions = computed(() => getEntityPermissions("ai_settings"));
  const aiSettingsSection = computed(() => {
    if (!workspace.permissionsReady) return null;
    if (!aiSettingsPermissions.value.active) return null;

    // Without `read`, the section stays visible (#3230) but its pages are unreachable (#3416) —
    // show the entries disabled rather than letting them look clickable and bounce to Home.
    const disabled = !aiSettingsPermissions.value.read;

    return {
      label: $t("common.label.aiSettings"),
      layoutStoreKey: "aiSettings",
      children: [
        {
          label: $t("settings.aiSettings.label.common"),
          layoutStoreKey: "aiSettingsCommon",
          route: { name: Routes.SETTINGS_AI_COMMON },
          disabled,
        },
        {
          label: $t("settings.aiSettings.label.fileClassification"),
          layoutStoreKey: "aiSettingsFileClassification",
          route: { name: Routes.SETTINGS_AI_FILE_CLASSIFICATION },
          disabled,
        },
      ],
    };
  });

  return computed<NavNode | null>(() => {
    const children = [];
    if (workspace.tenant?.accountingEnabled) {
      children.push(...(accountingSection.value ? [accountingSection.value] : []));
    }

    children.push(
      ...(organizationsSection.value ? [organizationsSection.value] : []),
      ...(modulesSection.value ? [modulesSection.value] : []),
      ...(usersSection.value ? [usersSection.value] : []),
      // TODO: Remove when Email feature is ready for PROD !!
      ...(emailFeatureConfig.enabled && servicesSection.value ? [servicesSection.value] : []),
      ...(aiSettingsSection.value ? [aiSettingsSection.value] : []),
      ...(permissionSection.value ? [permissionSection.value] : []),
      ...(customDataSection.value ? [customDataSection.value] : []),
    );

    if (children.length === 0) return null;

    return {
      label: $t("common.label.settings"),
      icon: "phosphor/gear",
      layoutStoreKey: "settings",
      children: children,
    };
  });
}
